import { useQuery } from '@tanstack/react-query';

import { type DocumentFile, type DocumentFolder } from './use-document-listing';

import { apiService } from '@/services/api-service';
import {
	associationDocumentPath,
	type AssociationDocumentPathFields,
} from '@/utils/document-folder-path';

/**
 * Portfolio-wide document browsing (issue #403).
 *
 * ## Why a column browser needs no new endpoint per column
 *
 * A path like `Condos/Riviera/Contracts` is just FOUR independent listings — the root,
 * `Condos`, `Condos/Riviera`, and the full path — one per rendered column. Each is its own
 * query, cached on its own path key, so:
 *
 * - navigating deeper only fetches the NEW column; the ones already on screen are hits
 * - clicking back up the tree is instant, because those keys are still warm
 * - two sibling folders opened in turn share every ancestor column between them
 *
 * That is the whole reason columns are cheap here. A single "give me the tree" endpoint
 * would refetch the entire chain on every click and cache none of it.
 */

/** How an association came to be filed in a folder. */
export type FolderMapping = 'pinned' | 'derived';

/** One association filed in a folder, as the listing resolves it. */
export interface BrowseAssociation {
	id: string;
	name: string;
	archived: boolean;
	type: string | null;
	status: string | null;
	numberOfUnits: number | null;
	/** `pinned` = an explicit `dropbox_path`; `derived` = the association's name. */
	mapping: FolderMapping;
	pinnedPath?: string | null;
	manager?: { id: string; name: string; color: string | null };
}

/** A folder row, plus the association identity when this level lists associations. */
export interface BrowseFolder extends DocumentFolder {
	/**
	 * Only at the ROOT: 'numbered' — one of SBS's hand-numbered working folders
	 * (`1-CONDOMINIUMS` … `14- Nyree`) — or 'other' for everything else on the drive. The
	 * browser rules the two apart.
	 *
	 * Grouped server-side so the numbering rule is not restated in the UI; the client only
	 * has to notice where the group changes.
	 */
	group?: 'numbered' | 'other';
	/** The parsed prefix (`1-CONDOMINIUMS` → 1), or null. Drives the root ordering. */
	folderNumber?: number | null;
	/**
	 * Presentation overrides from `document_folder_settings` (migration 253). All optional —
	 * the drive supplies the folders and a row here is decoration.
	 *
	 * There is deliberately NO name override — a folder is called whatever Dropbox calls it.
	 */
	/** Ionicons glyph. Null falls through to the name-keyed `folderVisual()` map. */
	icon?: string | null;
	color?: string | null;
	sortOrder?: number | null;
	/** 'condo' | 'HOA' | 'archived' — set only on roots that hold communities. */
	associationType?: string | null;
	associationId?: string;
	/** Set at the association level: this community is retired (it lives under `Archived`). */
	archived?: boolean;
	/**
	 * The assigned property manager, joined server-side.
	 *
	 * Resolved in the listing query rather than per row: the associations list renders
	 * `<UserName id={assignedTo} />` per item, which is one user fetch each — 32 requests
	 * for the Condos column alone.
	 */
	manager?: { id: string; name: string; color: string | null };
	/**
	 * EVERY association filed in this folder. More than one is legitimate — a takeover can
	 * leave two entities sharing one folder. The scalar fields above are `associations[0]`.
	 */
	associations?: BrowseAssociation[];
	/**
	 * An association-bucket folder no association resolves to.
	 *
	 * Listed rather than hidden, marked, and sorted last by the server. Usually a renamed
	 * community, a takeover's leftovers, or a hand-made folder — all needing a human to look
	 * inside and say what they are.
	 */
	unlinked?: boolean;
}

export interface BrowseListing {
	path: string[];
	folders: BrowseFolder[];
	files: DocumentFile[];
	associationId?: string;
	/** Every association this listing is INSIDE. Set from depth 2 down. */
	associations?: BrowseAssociation[];
	unavailable?: 'not-configured';
}

export const browseKeys = {
	all: ['documentBrowse'] as const,
	/**
	 * Keyed on the '/'-joined path — a primitive, so it can't re-key every render.
	 *
	 * `showHidden` is part of the key: the two views are different answers for the same
	 * folder, and sharing one entry would show the filtered list after toggling hidden on
	 * (or worse, leak hidden entries into the default view from a warm cache).
	 */
	level: (path: string, showHidden: boolean) =>
		[...browseKeys.all, 'level', path, showHidden ? 'all' : 'visible'] as const,
};

/** One column's contents. `path` is '/'-joined; '' is the portfolio root. */
export function useBrowseLevel(path: string, enabled = true, showHidden = false) {
	return useQuery<BrowseListing>({
		queryKey: browseKeys.level(path, showHidden),
		queryFn: () => {
			const params = new URLSearchParams();
			if (path) params.set('path', path);
			if (showHidden) params.set('hidden', '1');
			const query = params.toString();
			return apiService.fetchApi(
				`/documents/browse${query ? `?${query}` : ''}`,
			) as Promise<BrowseListing>;
		},
		enabled,
		// The drive is the source of truth and staff edit it directly, so don't hold a
		// listing long enough to contradict what they see in Dropbox. Long enough, though,
		// that walking back up the tree is instant.
		staleTime: 30_000,
		retry: false,
	});
}

/**
 * Every column for a path, as one array.
 *
 * `Condos/Riviera` renders three columns: the root, inside `Condos`, inside
 * `Condos/Riviera`.
 *
 * The calls are written out rather than generated in a loop because React requires the
 * same hooks in the same order on every render. Mapping over a variable-length path would
 * change how many `useQuery` calls run as the user navigates — the exact thing the rules
 * of hooks forbid. A fixed ceiling with `enabled` gating gives the same behaviour: the
 * hooks beyond the current depth exist but issue no request.
 *
 * Eight is deeper than the taxonomy goes (`Type/Association/Category/Year` is four, and an
 * owner path is five), with room for a folder someone nested by hand.
 */
export function useBrowseColumns(segments: string[], showHidden = false) {
	const depth = segments.length;
	const at = (i: number) => segments.slice(0, i).join('/');

	const c0 = useBrowseLevel(at(0), depth >= 0, showHidden);
	const c1 = useBrowseLevel(at(1), depth >= 1, showHidden);
	const c2 = useBrowseLevel(at(2), depth >= 2, showHidden);
	const c3 = useBrowseLevel(at(3), depth >= 3, showHidden);
	const c4 = useBrowseLevel(at(4), depth >= 4, showHidden);
	const c5 = useBrowseLevel(at(5), depth >= 5, showHidden);
	const c6 = useBrowseLevel(at(6), depth >= 6, showHidden);
	const c7 = useBrowseLevel(at(7), depth >= 7, showHidden);

	return [c0, c1, c2, c3, c4, c5, c6, c7].slice(0, depth + 1);
}

/**
 * ONE association's top-level folders, for the Quick Actions row on its dashboard.
 *
 * Just a browse level, addressed by the association's DERIVED path — which is why it lives
 * beside `useBrowseLevel` rather than in the account-scoped listing hooks. It shares the
 * browser's cache entry too, so opening the dashboard warms the column the folder cards
 * link into (and vice versa).
 *
 * Takes the association ROW, not an id: the path is a pure function of the row the caller
 * already holds in TanStack cache (`useAssociation`), so there is nothing to ask the server.
 *
 * `unavailable` is the only "no folders, and that is not the folder's fault" signal left.
 * The old 409 "not provisioned" state went with the recorded folder ids — a path always
 * exists, so an association whose folder has never been created simply lists empty.
 */
export function useAssociationDocumentFolders(association: AssociationDocumentPathFields | null | undefined) {
	const path = association ? associationDocumentPath(association) : '';
	const query = useBrowseLevel(path, !!path);
	return {
		path,
		folders: query.data?.folders ?? [],
		isLoading: query.isLoading,
		unavailable: query.data?.unavailable,
	};
}
